import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { User } from '../users';
import { TruckCompany } from './trucksCompanies';
import { TruckType } from './trucksTypes';
import { DailyTruckRide } from '../dailyReports/dailyTrucksRides';
import { Site } from '../sites/sites';

export interface Truck {
    crudStatus: number;
    _id: string;
    codeProC: string;
    designation: string;
    plateNumber: string;
    truckTypeId: TruckType;
    truckCompanyId: TruckCompany;
    userId?: User;
    isIncomplete?: boolean;
    price?: number;
    priceType?: number;
    usageCount?: {
        total: number;
        dailyTrucksPlanned: number;
        dailyTrucksRides: number;
    };
    model?: string;
    startDate?: string;
    endDate?: string;
    updatedAt?: string;
    createdAt?: string;
    baseName?: string;
}

export interface TrucksResponse {
    message: string;
    data: Truck[];
}

export interface TruckResponse {
    message: string;
    data: Truck;
}

export interface GetAllTrucksQueries {
    active?: boolean;
    withUsage?: boolean;
    truckCompanyId?: string;
}

export const getAllTrucks = async (queries?: GetAllTrucksQueries): Promise<TrucksResponse> => {
    try {
        const response: AxiosResponse<TrucksResponse> = await apiClient.get('/trucks', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getTrucksByCompanyId = async (truckCompanyId: string): Promise<TrucksResponse> => {
    try {
        const response: AxiosResponse<TrucksResponse> = await apiClient.get('/trucks/company/' + truckCompanyId);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateTruckBody {
    designation?: string;
    codeProC?: string;
    truckTypeId?: string;
    userId?: string | null;
    crudStatus?: number;
    plateNumber?: string;
    truckCompanyId?: string;
    model?: string;
    startDate?: string | null;
    endDate?: string | null;
    baseName?: string;
    priceType?: number | null;
    price?: number | null;
}

export const updateTruck = async (truckId: string, data: UpdateTruckBody): Promise<TruckResponse> => {
    try {
        const response: AxiosResponse<TruckResponse> = await apiClient.patch('/trucks/' + truckId, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateTruckBody {
    designation: string;
    codeProC: string;
    truckTypeId: string;
    plateNumber: string;
    userId?: string;
    truckCompanyId: string;
    crudStatus?: number;
    model?: string;
    startDate?: string | null;
    endDate?: string | null;
    baseName?: string;
    priceType?: number | null;
    price?: number | null;
}

export const createTruck = async (data: CreateTruckBody): Promise<TruckResponse> => {
    try {
        const response: AxiosResponse<TruckResponse> = await apiClient.post('/trucks', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface TruckSupervision {
    rides: (DailyTruckRide & { activitySite: Site })[];
    truck: Truck;
}

export interface TruckSupervisionResponse {
    message: string;
    data: TruckSupervision[];
}

export const getTrucksSupervisionByDate = async (date: string): Promise<TruckSupervisionResponse> => {
    try {
        const response: AxiosResponse<TruckSupervisionResponse> = await apiClient.get(`/trucks/supervision/${date}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface SiteSupervision {
    site: Site;
    trucks: SiteTruckSupervision[];
}

export interface SiteTruckSupervision {
    truck: Truck;
    rides: DailyTruckRide[];
}

export interface SiteSupervisionResponse {
    message: string;
    data: SiteSupervision[];
}

export const getSiteTrucksSupervisionByDate = async (date: string): Promise<SiteSupervisionResponse> => {
    try {
        const response: AxiosResponse<SiteSupervisionResponse> = await apiClient.get(`/trucks/sites/supervision/${date}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
